import type { AggregatedOrderView } from '@/types/api/aggregated/order'
import type { AggregatedRfqView } from '@/types/api/aggregated/rfq'

import { convertHwmToDate } from '@/shared/helpers/convert-hwm-to-date'
import { getMarketDayStart, getTodayStartHwm } from '@/utils/dates/dateHelpers'

export const isEventOutsideRange = (
  eventTime: string | undefined,
  startTimeMillis: number,
  endTimeMillis?: number,
) => {
  if (!eventTime) {
    return false
  }
  const eventTimeMillis = new Date(eventTime).getTime()
  const isBeforeStartTime = eventTimeMillis < startTimeMillis
  const isAfterEndTime = endTimeMillis ? eventTimeMillis > endTimeMillis : false
  return isBeforeStartTime || isAfterEndTime
}

/**
 * Calculates the high water mark (HWM) across all views, leveraging
 * the fact that the array is sorted in ascending order by msg_number.
 * @param views Array of views sorted in ascending order by msg_number
 * @returns HWM as a bigint
 */
export const calculateInitialHwm = (
  views: AggregatedOrderView[] | AggregatedRfqView[],
): bigint => {
  const defaultHwm = getTodayStartHwm()
  if (views.length === 0) {
    return BigInt(defaultHwm)
  }
  // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
  const latestMsgNumber = views[views.length - 1]!.latest_msg_number
  if (!latestMsgNumber) {
    return BigInt(defaultHwm)
  }
  return BigInt(latestMsgNumber) + 1n
}

export const getFilterStartAndEndTimes = (
  startHwm?: string,
  endHwm?: string,
) => {
  let filterStartTime = getMarketDayStart()
  if (startHwm && startHwm !== getTodayStartHwm()) {
    // Convert HWM format (YYYYMMDD0000000000) back to date
    filterStartTime = convertHwmToDate(startHwm).getTime()
  }
  let filterEndTime: number | undefined
  if (endHwm) {
    // The end date is inclusive: filter through the last millisecond of the
    // end HWM's date. Day rollover via the Date constructor stays DST-safe.
    const endDate = convertHwmToDate(endHwm)
    filterEndTime =
      new Date(
        endDate.getFullYear(),
        endDate.getMonth(),
        endDate.getDate() + 1,
      ).getTime() - 1
  }
  return {
    filterStartTime,
    filterEndTime,
  }
}
